<script lang="ts">
    import * as Card from "@/lib/components/ui/card";
    import LoginForm from "./LoginForm.svelte";
    import SignupForm from "./SignupForm.svelte";
    import ForgotPasswordForm from "./ForgotPasswordForm.svelte";
    import ResetPasswordForm from "./ResetPasswordForm.svelte";
    import CompleteSocialSignupForm from "./CompleteSocialSignupForm.svelte";
    import SsoMarketingPanel from "./SsoMarketingPanel.svelte";
    import CookieBanner from "./CookieBanner.svelte";
    import { getRequest } from "@/Util";

    // Props from Blade
    export let mode: 'login' | 'signup' | 'forgot' | 'reset' | 'activated' | 'password_reset' | 'social_complete' = 'login'
    /** Prefill for the social signup completion form; null in every other mode. */
    export let social: {
        provider: string,
        providerLabel: string,
        email: string | null,
        emailLocked: boolean,
        username: string,
        firstName: string,
        lastName: string,
    } | null = null
    export let oauth: number | null = null
    export let redirect: number | null = null
    export let recaptchaKey: string = ''
    export let activated: boolean = false
    export let status: string | null = null
    export let token: string = ''
    export let email: string = ''
    export let flashes: Record<string, any> = {}
    /** Email of the signed-in user, set only on the forgot-password page. */
    export let signedInEmail: string | null = null
    /** Messages from the server-side $errors bag (login failures, social login failures, …). */
    export let errors: string[] = []

    type Mode = 'login' | 'signup' | 'forgot' | 'reset' | 'activated' | 'password_reset' | 'signup_success' | 'social_complete'

    let currentMode: Mode = mode
    let signupEmail = ''

    // -------------------------------------------------------------------------
    // Cookie notice
    //
    // Only strictly necessary cookies (session, CSRF) and Google reCAPTCHA for
    // spam prevention are used on this page, so the notice is informational: it
    // records that it was shown rather than gating anything behind consent.
    // -------------------------------------------------------------------------
    const NOTICE_KEY = 'xert_cookie_notice_ack'

    function noticeAcknowledged(): boolean {
        try { return localStorage.getItem(NOTICE_KEY) === '1' } catch { return false }
    }

    let showCookieNotice = !noticeAcknowledged()

    function acknowledgeCookieNotice() {
        try { localStorage.setItem(NOTICE_KEY, '1') } catch {}
        showCookieNotice = false
    }

    // -------------------------------------------------------------------------
    // Banners
    // -------------------------------------------------------------------------
    type BannerType = 'success' | 'info' | 'error'
    type BannerAction = 'resend_activation' | 'reactivate_account' | null

    let bannerMsg = ''
    let bannerType: BannerType = 'success'
    let bannerAction: BannerAction = null
    let actionResult = ''

    function setBanner(msg: string, type: BannerType = 'success', action: BannerAction = null) {
        bannerMsg = msg
        bannerType = type
        bannerAction = action
    }

    function clearBanner() {
        bannerMsg = ''
        bannerAction = null
        actionResult = ''
    }

    if (activated) {
        setBanner('Your account has been activated! Please sign in.', 'success')
    } else if (mode === 'password_reset') {
        setBanner('Your password has been reset. Please sign in with your new password.', 'success')
    } else if (flashes?.activation_required) {
        // The account exists but was never activated — offer to resend the email.
        setBanner('Please activate your account before signing in. Check your inbox for the activation link.', 'error', 'resend_activation')
    } else if (flashes?.reactivate_account) {
        setBanner('Your account has been deactivated. You can reactivate it and sign in again.', 'error', 'reactivate_account')
    } else if (flashes?.activation_resent) {
        setBanner(flashes.activation_resent, 'success')
    } else if (errors?.length) {
        // Login failures, social login failures, and anything else flashed to the
        // $errors bag. These used to be dropped on the floor.
        setBanner(errors.join(' '), 'error')
    } else if (status) {
        setBanner(status, 'info')
    } else if (flashes?.email) {
        setBanner(`An activation email has been sent to ${flashes.email}. Check your inbox (or spam folder).`, 'info')
    }

    // -------------------------------------------------------------------------
    // Banner actions
    // -------------------------------------------------------------------------
    let actionBusy = false

    async function resendActivation() {
        if (!flashes?.activation_username) return
        actionBusy = true
        try {
            const res = await fetch(`/auth/reactivate/${encodeURIComponent(flashes.activation_username)}`, getRequest())
            const data = await res.json().catch(() => ({}))
            actionResult = res.ok && data?.success
                ? 'Activation email sent — check your inbox (and spam folder).'
                : 'We could not send the activation email. Please contact support@xertonline.com.'
        } catch {
            actionResult = 'A network error occurred. Please try again.'
        } finally {
            actionBusy = false
            bannerAction = null
        }
    }

    async function reactivateAccount() {
        if (!flashes?.reactivate_user_id) return
        actionBusy = true
        try {
            const res = await fetch('/account/settings/reactivate-account', {
                ...getRequest(),
                method: 'POST',
                body: JSON.stringify({ id: flashes.reactivate_user_id }),
            })
            const data = await res.json().catch(() => ({}))
            actionResult = res.ok && data?.message
                ? data.message
                : 'We could not reactivate your account. Please contact support@xertonline.com.'
        } catch {
            actionResult = 'A network error occurred. Please try again.'
        } finally {
            actionBusy = false
            bannerAction = null
        }
    }

    function handleSignupSuccess(email: string) {
        signupEmail = email
        currentMode = 'signup_success'
    }

    function handleResetSuccess(redirectTo: string) {
        // Resetting also signs the user in, so send them straight into the app
        // rather than asking them to log in again.
        window.location.href = redirectTo
    }

    $: bannerClasses =
        bannerType === 'success' ? 'text-green-700 bg-green-50 border-green-200'
        : bannerType === 'error' ? 'text-destructive bg-destructive/10 border-destructive/20'
        : 'text-blue-700 bg-blue-50 border-blue-200'
</script>

<div class="twp min-h-screen lg:grid lg:grid-cols-2">
    <!-- Left: auth form panel -->
    <div class="flex flex-col min-h-screen">
        <!-- Logo + card centered together as one group, so the whitespace above and below stays balanced -->
        <div class="flex flex-1 flex-col items-center justify-center gap-8 px-4 py-8 sm:px-8">
            <!-- Mobile-only logo -->
            <img src="/assets/images/logos/XertLogosV3/Xert%20V3%20Logo%20-%20Teal.svg" alt="Xert" class="h-10 sm:h-12 w-auto lg:hidden" />

            <Card.Root class="w-full max-w-sm shadow-md">
                <Card.Content class="pt-6">
                    <!-- Desktop logo inside card -->
                    <div class="hidden lg:flex justify-center mb-6">
                        <img src="/assets/images/logos/XertLogosV3/Xert%20V3%20Logo%20-%20Teal.svg" alt="Xert" class="h-8" />
                    </div>

                    <!-- Flash banner -->
                    {#if bannerMsg}
                        <div class="mb-4 text-sm rounded-md px-3 py-2 border {bannerClasses}">
                            <p>{bannerMsg}</p>

                            {#if bannerAction === 'resend_activation' && flashes?.activation_username}
                                <button
                                    type="button"
                                    class="mt-2 font-medium underline underline-offset-2 disabled:opacity-60"
                                    on:click={resendActivation}
                                    disabled={actionBusy}
                                >
                                    {actionBusy ? 'Sending…' : 'Resend activation email'}
                                </button>
                            {/if}

                            {#if bannerAction === 'reactivate_account' && flashes?.reactivate_user_id}
                                <button
                                    type="button"
                                    class="mt-2 font-medium underline underline-offset-2 disabled:opacity-60"
                                    on:click={reactivateAccount}
                                    disabled={actionBusy}
                                >
                                    {actionBusy ? 'Reactivating…' : 'Reactivate my account'}
                                </button>
                            {/if}

                            {#if actionResult}
                                <p class="mt-2 font-medium">{actionResult}</p>
                            {/if}
                        </div>
                    {/if}

                    {#if currentMode === 'login' || currentMode === 'activated' || currentMode === 'password_reset'}
                        <LoginForm
                            {oauth}
                            {redirect}
                            onForgot={() => { currentMode = 'forgot'; clearBanner() }}
                            onSignup={() => { currentMode = 'signup'; clearBanner() }}
                        />

                    {:else if currentMode === 'signup'}
                        <SignupForm
                            {recaptchaKey}
                            onLogin={() => { currentMode = 'login'; clearBanner() }}
                            onSuccess={handleSignupSuccess}
                        />

                    {:else if currentMode === 'social_complete' && social}
                        <CompleteSocialSignupForm
                            provider={social.provider}
                            providerLabel={social.providerLabel}
                            email={social.email}
                            emailLocked={social.emailLocked}
                            username={social.username}
                            firstName={social.firstName}
                            lastName={social.lastName}
                        />

                    {:else if currentMode === 'forgot'}
                        <!-- A signed-in user has no sign-in form to go back to: /auth would redirect them away. -->
                        <ForgotPasswordForm
                            initialEmail={signedInEmail ?? ''}
                            onBack={signedInEmail ? null : () => { currentMode = 'login'; clearBanner() }}
                        />

                    {:else if currentMode === 'reset'}
                        <ResetPasswordForm
                            {token}
                            {email}
                            onSuccess={handleResetSuccess}
                        />

                    {:else if currentMode === 'signup_success'}
                        <div class="space-y-6 text-center">
                            <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                                <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                                </svg>
                            </div>
                            <div class="space-y-2">
                                <h2 class="text-xl font-semibold">Check your inbox</h2>
                                <p class="text-sm text-muted-foreground">
                                    We've sent an activation link to <span class="font-medium text-foreground">{signupEmail}</span>.
                                    Click the link to activate your account and start your free trial.
                                </p>
                                <p class="text-xs text-muted-foreground mt-2">Didn't get it? Check your spam folder.</p>
                            </div>
                            <button
                                type="button"
                                class="text-sm text-xerteal-600 hover:underline"
                                on:click={() => { currentMode = 'login'; clearBanner() }}
                            >
                                Back to sign in
                            </button>
                        </div>
                    {/if}
                </Card.Content>
            </Card.Root>
        </div>

        <!-- Footer -->
        <div class="py-4 px-8 text-center text-xs text-muted-foreground lg:hidden">
            © {new Date().getFullYear()} Baron Biosystems Inc.
        </div>
    </div>

    <!-- Right: marketing panel (desktop only) -->
    <SsoMarketingPanel />
</div>

<!-- Cookie notice — informational, dismissal remembered -->
{#if showCookieNotice}
    <CookieBanner onAcknowledge={acknowledgeCookieNotice} />
{/if}
